import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Receipt,
  Clock,
  Bell,
  WarningCircle,
  Warning,
  CheckCircle,
  CurrencyGbp,
  PaperPlaneTilt,
  Phone,
  Handshake,
  FolderOpen,
  CircleNotch,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fetchMatterFinancialState, type MatterFinancialState, type FinancialActionType } from '@/lib/mattersApi';
import { sendFeeNoteReminder } from '@/lib/feeNotesApi';
import { getApiErrorMessage } from '@/lib/api';

interface Props {
  matterId: string;
  /** True when the viewer may act on finances (not a junior clerk). */
  canAct: boolean;
  /** Delegate modal-backed actions to the parent (raise fee note / close / chase). */
  onAction: (type: FinancialActionType, feeNoteId?: string) => void;
}

// Visual tone per professional state — calm by default, louder only as risk rises.
const TONE: Record<
  MatterFinancialState['state'],
  { ring: string; chip: string; icon: typeof Receipt; accent: string }
> = {
  no_billing:              { ring: 'border-border',        chip: 'text-muted-foreground',        icon: CurrencyGbp,   accent: 'text-muted-foreground' },
  needs_billing:           { ring: 'border-brand-200',     chip: 'text-brand-700',               icon: Receipt,       accent: 'text-brand-600 dark:text-brand-400' },
  awaiting_payment:        { ring: 'border-border',        chip: 'text-muted-foreground',        icon: Clock,         accent: 'text-muted-foreground' },
  payment_needs_attention: { ring: 'border-amber-200 dark:border-amber-500/30',     chip: 'text-amber-800 dark:text-amber-300',               icon: Bell,          accent: 'text-amber-600' },
  payment_overdue:         { ring: 'border-orange-200 dark:border-orange-500/30',    chip: 'text-orange-800 dark:text-orange-300',              icon: WarningCircle, accent: 'text-orange-600' },
  payment_at_risk:         { ring: 'border-red-200 dark:border-red-500/30',       chip: 'text-red-800 dark:text-red-300',                 icon: Warning,       accent: 'text-red-600' },
  financially_complete:    { ring: 'border-emerald-200 dark:border-emerald-500/30',   chip: 'text-emerald-800 dark:text-emerald-300',             icon: CheckCircle,   accent: 'text-emerald-600' },
};

const ACTION_ICON: Record<FinancialActionType, typeof Receipt | null> = {
  raise_fee_note: Receipt,
  send_reminder: PaperPlaneTilt,
  follow_up: Handshake,
  call_solicitor: Phone,
  close_matter: FolderOpen,
  none: null,
};

export function MatterMoneySituation({ matterId, canAct, onAction }: Props) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const stateQ = useQuery<MatterFinancialState>({
    queryKey: ['matter-financial-state', matterId],
    queryFn: () => fetchMatterFinancialState(matterId),
    staleTime: 30_000,
  });

  const reminderMut = useMutation({
    mutationFn: (feeNoteId: string) => sendFeeNoteReminder(feeNoteId),
    onSuccess: () => {
      toast.success('Payment reminder sent to the solicitor');
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['matter-financial-state', matterId] });
      qc.invalidateQueries({ queryKey: ['matter-financial-summary', matterId] });
      qc.invalidateQueries({ queryKey: ['matter-fee-notes', matterId] });
      qc.invalidateQueries({ queryKey: ['matter-payment-story', matterId] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not send the reminder')),
  });

  const s = stateQ.data;

  if (stateQ.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border px-4 py-3 text-xs text-muted-foreground">
        <CircleNotch size={13} className="animate-spin" />
        Reading the money…
      </div>
    );
  }
  if (!s) return null;

  const tone = TONE[s.state];
  const Icon = tone.icon;
  const ActionIcon = ACTION_ICON[s.recommendedAction.type];
  const hasAction = s.recommendedAction.type !== 'none' && canAct;

  function runAction() {
    const a = s!.recommendedAction.type;
    if (a === 'send_reminder') {
      setConfirming(true);
      return;
    }
    if ((a === 'follow_up' || a === 'call_solicitor')) {
      onAction(a, s!.focus?.feeNoteId);
      return;
    }
    onAction(a);
  }

  return (
    <div className={`rounded-md border ${tone.ring} bg-card`}>
      <div className="flex items-start justify-between gap-4 px-4 py-3">
        {/* Situation */}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Icon size={15} weight="duotone" className={tone.accent} />
            <span className={`text-xs font-semibold tracking-tight ${tone.chip}`}>{s.label}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            {s.facts.map((f, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-border">·</span>}
                <span className={i === 0 ? 'font-medium text-foreground' : ''}>{f}</span>
              </span>
            ))}
          </div>
        </div>

        {/* One recommended action */}
        {hasAction && !confirming && (
          <button
            type="button"
            onClick={runAction}
            disabled={reminderMut.isPending}
            className={`flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
              s.state === 'payment_at_risk'
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-brand-600 text-white hover:bg-brand-700'
            } disabled:opacity-60`}
          >
            {ActionIcon && <ActionIcon size={13} weight="bold" />}
            {s.recommendedAction.label}
          </button>
        )}
      </div>

      {/* Inline confirm before emailing the solicitor (outward action) */}
      {confirming && (
        <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/40 px-4 py-2.5">
          <span className="text-xs text-muted-foreground">
            Email a payment reminder to the instructing solicitor?
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={reminderMut.isPending}
              className="rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => s.focus && reminderMut.mutate(s.focus.feeNoteId)}
              disabled={reminderMut.isPending || !s.focus}
              className="flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {reminderMut.isPending ? <CircleNotch size={13} className="animate-spin" /> : <PaperPlaneTilt size={13} weight="bold" />}
              Send reminder
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
